import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component } from "preact";
import { inviteLookupResponse } from "~/backend/validate.js";
import { apiRequest, ApiError, query, response } from "~/lib/api.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import { search } from "~/util/router.js";
import { anonymousId, trackPageView } from "~/util/segment.js";
import { object, optional, parse, string } from "~/util/validate.js";
import Banner from "~/component/Banner.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import PasswordRules, { RULES } from "~/component/PasswordRules.js";
import PublicLayout, { FooterPrompt } from "./PublicLayout.js";
import button from "~/style/button.js";
import input from "~/style/input.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Signup.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Signup.module.css", import.meta.url);
const css = adopt(sheet);
export class SignUp extends Component {
  lookup = query(() => ({
    key: ["/v2/invites/lookup", {
      token: search.value["token"] ?? ""
    }],
    enabled: Boolean(search.value["token"])
  }), {
    schema: response(inviteLookupResponse),
    noAuth: true
  });
  password = signal("");
  /**
   * @type {import("@preact/signals").Signal<
   * 	null | { kind: "email_sent"; email: string } | { kind: "invite_accepted" }
   * >}
   */
  signupResult = signal(null);
  signUp = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      name: string,
      email: string,
      password: optional(string)
    }), Object.fromEntries(form.entries()));
    const invite_token = search.value.token;
    if (invite_token) {
      await apiRequest("POST", `/v2/signup/invite`, {
        json: {
          name: data.name,
          invite_token,
          password: data.password,
          anonymous_id: anonymousId()
        }
      });
      this.signupResult.value = {
        kind: "invite_accepted"
      };
    } else {
      await apiRequest("POST", `/v2/signup`, {
        json: {
          name: data.name,
          email: data.email,
          password: (/** @type {string} */data.password),
          anonymous_id: anonymousId()
        }
      });
      this.signupResult.value = {
        kind: "email_sent",
        email: data.email
      };
    }
  });

  /** @override */
  componentDidMount() {
    trackPageView();
  }
  render() {
    if (this.lookup.error?.code === "not_found") {
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
    const result = this.signupResult.value;
    if (result?.kind === "email_sent") {
      return __h(SignupSentState, {
        sentTo: result.email
      });
    }
    if (result?.kind === "invite_accepted") {
      return __h(InviteAcceptedState, {});
    }
    const lookup = this.lookup.data?.data;
    if (search.value["token"] && !lookup && this.lookup.loading) {
      return;
    }
    const hide_password = lookup && lookup.is_sso_enabled;
    const error = this.signUp.error && SignUpError.from(this.signUp.error);
    return [__h(SignupTokenBanner, {
      lookup: lookup
    }), __h(PublicLayout, {
      title: "Sign up for Phaze",
      onSubmit: this.signUp.invoke
    }, __h(Field, {
      "for": "name",
      label: "Full name",
      error: error?.fields.name?.message,
      validity: {
        tooShort: "Names must be at least two characters long.",
        patternMismatch: "Names may only contain letters, spaces, hyphens or apostrophes."
      }
    }, __h("input", {
      "class": input.input,
      id: "name",
      type: "text",
      name: "name",
      minlength: 2,
      maxlength: 100,
      pattern: "[\xC0-\u017EA-Za-z0-9 _'\\\\-]+",
      required: true
    })), __h(Field, {
      "for": "email",
      label: "Email",
      error: error?.fields.email?.message
    }, __h("input", {
      "class": input.input,
      id: "email",
      type: "email",
      name: "email",
      required: true,
      value: lookup?.email,
      readonly: Boolean(lookup?.email)
    })), !hide_password && __h(Field, {
      "for": "password",
      label: "Password",
      error: error?.fields.password?.message,
      validity: {
        /* this is handled by the password rules component */
        patternMismatch: false
      }
    }, __h(PasswordRules, {
      password: this.password.value
    }, __h("input", {
      "class": input.input,
      id: "password",
      type: "password",
      name: "password",
      required: true,
      ...RULES,
      onInput: async (/** @type {{ currentTarget: HTMLInputElement }} */e) => {
        this.password.value = e.currentTarget.value;
      }
    }))), __h("p", {
      "class": css.error
    }, error?.message), __h("p", {
      "class": css.fineprint
    }, "By signing up, you agree to our ", __h("a", {
      href: "https://phaze.app/terms"
    }, "Terms"), __h("span", {}, " and "), __h("a", {
      href: "https://phaze.app/privacy"
    }, "Privacy Policy")), __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.signUp.loading
    }, this.lookup.data ? "Accept invite" : "Sign up"), __h(FooterPrompt, {}, "Already have an account? ", __h("a", {
      href: "/login/"
    }, "Log in")))];
  }
}

/**
 * @extends {Component<SignupTokenBannerProps>}
 *
 * @typedef {object} SignupTokenBannerProps
 * @property {inviteLookupResponse} [lookup]
 */
class SignupTokenBanner extends Component {
  render() {
    if (!this.props.lookup) return;
    return __h("div", {
      "class": css.signupTokenBanner,
      "data-expired": this.props.lookup.is_expired
    }, __h("span", {}, this.props.lookup.is_expired ? [__h("strong", {}, "This invite is expired."), " Please contact your organization\n\t\t\t\t\t\t\tadministrator to resend the invite."] : [__h("strong", {}, this.props.lookup.sender_name), " has invited you to", " ", __h("strong", {}, this.props.lookup.enterprise_name), "!"]));
  }
}

/** @param {{ sentTo: string }} props */
function SignupSentState({
  sentTo
}) {
  return __h(PublicLayout, {
    title: "Sign up for Phaze"
  }, __h(Banner, {
    icon: __h(Icon, {
      name: "check"
    }),
    kind: "success"
  }, " Signup email sent! "), __h("p", {}, "Check your email at ", sentTo, "."), __h("a", {
    "class": button.button,
    href: "/login/"
  }, "Log in"));
}
function InviteAcceptedState() {
  return __h(PublicLayout, {
    title: "Sign up for Phaze"
  }, __h(Banner, {
    icon: __h(Icon, {
      name: "check"
    }),
    kind: "success"
  }, " Account created! "), __h("a", {
    "class": button.button,
    href: "/login/"
  }, "Log in"));
}
export class SignUpConfirm extends Component {
  confirm = new PromiseSignal(async token => await apiRequest("POST", "/v2/signup/confirm", {
    json: {
      token
    }
  }));
  render() {
    const error = this.confirm.error && ConfirmError.from(this.confirm.error);
    const token = search.value.token;
    const content = () => {
      if (!token) {
        return [" ", __h("p", {}, "Invalid email confirmation link."), " "];
      }
      if (this.confirm.success) {
        return [__h(Banner, {
          icon: __h(Icon, {
            name: "check"
          }),
          kind: "success"
        }, " Account confirmed! "), __h("a", {
          "class": button.button,
          href: "/login/"
        }, "Log in")];
      }
      return [__h("p", {}, "Click the button below to confirm your account."), __h(Button, {
        level: "primary",
        loading: this.confirm.loading,
        onClick: () => this.confirm.invoke(token).catch(() => {})
      }, "Confirm account"), error ? __h("p", {
        "class": css.error
      }, error.message) : null];
    };
    return __h(PublicLayout, {
      title: "Sign up for Phaze",
      "class": util.textCenter
    }, content());
  }
}

/** @extends {ApiError<"name" | "email" | "password">} */
class SignUpError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        email_registered: "This email address is already registered."
      },
      name: {},
      email: {},
      password: {}
    };
  }
}
class ConfirmError extends ApiError {
  static invalid = "This account verification link is invalid, or the account has already been confirmed.";

  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        unknown_error: "Couldn't verify email.",
        forbidden: ConfirmError.invalid,
        not_found: ConfirmError.invalid,
        invalid_token: ConfirmError.invalid
      }
    };
  }
}